import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Camera, CheckCircle, Shield, ArrowLeft, Upload, RefreshCw, Clock, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SelfieVerification = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null); // pending, verified, rejected, not_submitted
  const [cameraActive, setCameraActive] = useState(false);

  useEffect(() => {
    checkVerificationStatus();
  }, []);

  const checkVerificationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/verification/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setVerificationStatus(data.status || 'not_submitted');
      setIsVerified(data.verified);
    } catch (error) {
      console.error('Failed to check verification status:', error);
      setVerificationStatus('not_submitted');
    } finally {
      setIsLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStream(mediaStream);
      setCameraActive(true);
    } catch (error) {
      toast.error('Unable to access camera. Please check permissions.');
      console.error('Camera error:', error);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setCameraActive(false);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      stopCamera();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const submitVerification = async () => {
    if (!capturedImage) {
      toast.error('Please capture or upload a selfie first');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/verification/selfie`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ selfie_image: capturedImage })
      });

      if (!response.ok) {
        throw new Error('Verification failed');
      }

      const data = await response.json();
      
      if (data.status === 'verified') {
        setIsVerified(true);
        setVerificationStatus('verified');
        toast.success(data.message || 'Verification successful!');
      } else if (data.status === 'pending') {
        setVerificationStatus('pending');
        toast.success('Selfie submitted! We\'ll review it shortly.');
      } else if (data.status === 'rejected') {
        setVerificationStatus('rejected');
        toast.error(data.message || 'Verification failed. Please try again.');
      }
    } catch (error) {
      toast.error('Verification failed. Please try again.');
      console.error('Verification error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Verified state
  if (isVerified || verificationStatus === 'verified') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-white border-gray-100 shadow-lg">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="font-display text-2xl uppercase mb-3 text-gray-900">Verified!</h2>
            <p className="font-body text-gray-500 mb-8">
              Your profile is now verified. Other users will see a verification badge on your profile.
            </p>
            <Button 
              onClick={() => navigate('/discover')}
              className="w-full bg-primary hover:bg-red-600"
            >
              Continue to Discover
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pending state
  if (verificationStatus === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-white border-gray-100 shadow-lg">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-100 flex items-center justify-center">
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
            <h2 className="font-display text-2xl uppercase mb-3 text-gray-900">Under Review</h2>
            <p className="font-body text-gray-500 mb-4">
              Your selfie verification is being reviewed. This usually takes a few minutes.
            </p>
            <p className="font-body text-sm text-gray-400 mb-8">
              We'll notify you once the review is complete.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={checkVerificationStatus}
                className="flex-1 border-gray-200"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Check Status
              </Button>
              <Button 
                onClick={() => navigate('/discover')}
                className="flex-1 bg-primary hover:bg-red-600"
              >
                Continue
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Rejected state
  if (verificationStatus === 'rejected') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-white border-gray-100 shadow-lg">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="font-display text-2xl uppercase mb-3 text-gray-900">Verification Failed</h2>
            <p className="font-body text-gray-500 mb-4">
              We couldn't verify your selfie. Please make sure:
            </p>
            <ul className="text-left font-body text-sm text-gray-500 mb-6 space-y-2 px-4">
              <li className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <span>Your face is clearly visible and well-lit</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <span>No sunglasses, masks, or heavy filters</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <span>The photo matches your profile pictures</span>
              </li>
            </ul>
            <Button 
              onClick={() => setVerificationStatus('not_submitted')}
              className="w-full bg-primary hover:bg-red-600"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-lg mx-auto px-6 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="font-display font-bold text-xl uppercase tracking-tight text-gray-900">
            Verify Your Profile
          </h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-8">
        {/* Info Card */}
        <Card className="mb-6 bg-white border-gray-100 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="font-display text-lg uppercase text-gray-900">
                  Selfie Verification
                </CardTitle>
                <CardDescription className="font-body text-gray-500">
                  Prove you're real and get a verified badge
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 font-body text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Take a clear selfie of your face</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Make sure your face is well-lit and visible</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>No filters, sunglasses, or masks</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Camera/Upload Area */}
        <Card className="bg-white border-gray-100 shadow-sm">
          <CardContent className="p-6">
            {capturedImage ? (
              <div className="space-y-4">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100">
                  <img 
                    src={capturedImage} 
                    alt="Captured selfie" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1 border-gray-200"
                    onClick={retakePhoto}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retake
                  </Button>
                  <Button 
                    className="flex-1 bg-primary hover:bg-red-600"
                    onClick={submitVerification}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Verifying...' : 'Submit'}
                  </Button>
                </div>
              </div>
            ) : cameraActive ? (
              <div className="space-y-4">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-900">
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    muted
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1 border-gray-200"
                    onClick={stopCamera}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-primary hover:bg-red-600"
                    onClick={capturePhoto}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Capture
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl border-2 border-dashed border-gray-200 aspect-[4/3] flex flex-col items-center justify-center bg-gray-50">
                  <Camera className="w-12 h-12 text-gray-300 mb-4" />
                  <p className="font-body text-gray-500 text-center px-4">
                    Take a selfie or upload a photo
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1 border-gray-200"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                  <Button 
                    className="flex-1 bg-primary hover:bg-red-600"
                    onClick={startCamera}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Take Selfie
                  </Button>
                </div>
                <input 
                  type="file" 
                  ref={fileInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hidden canvas for capturing */}
        <canvas ref={canvasRef} className="hidden" />
      </main>
    </div>
  );
};
